import { useEffect, useState } from "react";
import { apiDocsService } from "@/services/breeze/ApiDocsService";
import {
  OpenApiDocumentationSections,
  OpenApiDocumentationResponse,
} from "@/types/breeze/ApiDoc";
import { Box, Text } from "@radix-ui/themes";
import { SkeletonPage } from "@/components/ui/Skeleton";
import PageTemplate from "@/components/page-template/PageTemplate";
import Tabs from "@/components/ui/Tabs";
import { formatAnyName } from "@/utils/text";
import { useTheme } from "@/providers/ThemeProvider";
import { getAuthClient } from "@/lib/auth";
import SwaggerUI from "swagger-ui-react";
import "swagger-ui-react/swagger-ui.css";
import "@/styles/swagger-dark.css";

const authClient = getAuthClient();

interface OpenApiViewerProps {
  spec: OpenApiDocumentationResponse;
}

const OpenApiViewer = ({ spec }: OpenApiViewerProps) => {
  const { isDarkMode } = useTheme();

  // Interceptor to add authentication token to all requests
  const requestInterceptor = async (req: any) => {
    const { data } = authClient.getSession();
    const token = data?.session?.access_token;
    if (token) {
      req.headers.Authorization = `Bearer ${token}`;
    }
    return req;
  };

  return (
    <Box
      style={{ width: "100%" }}
      className={isDarkMode ? "swagger-ui-dark" : "swagger-ui-light"}
    >
      {/* @ts-ignore */}
      <SwaggerUI
        spec={spec}
        docExpansion="list"
        defaultModelsExpandDepth={1}
        defaultModelExpandDepth={1}
        displayOperationId={false}
        displayRequestDuration={true}
        tryItOutEnabled={true}
        filter={true}
        showExtensions={true}
        showCommonExtensions={true}
        requestInterceptor={requestInterceptor}
      />
    </Box>
  );
};

export default function OpenApiDocsPage() {
  const [docs, setDocs] = useState<OpenApiDocumentationSections | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedDocType, setSelectedDocType] = useState<string>("data-model");
  const [availableTabs, setAvailableTabs] = useState<string[]>([]);

  useEffect(() => {
    const fetchDocs = async () => {
      try {
        const openApiDocs = await apiDocsService.getDocs();
        setDocs(openApiDocs);

        // Set available tabs based on documentation structure
        const tabs = [
          "data-model",
          ...Object.keys(openApiDocs.module_documentation || {}),
        ];
        setAvailableTabs(tabs);
        if (!tabs.includes(selectedDocType)) {
          setSelectedDocType(tabs[0]);
        }
      } catch (error) {
        console.error("Failed to fetch OpenAPI documentation:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDocs();
  }, [selectedDocType]);

  const renderDocumentation = (spec: OpenApiDocumentationResponse) => {
    return (
      <Box>
        <OpenApiViewer spec={spec} />
      </Box>
    );
  };

  if (loading) {
    return <SkeletonPage title="API Documentation" />;
  }

  if (!docs) {
    return (
      <PageTemplate title="API Documentation">
        <Box p="4">
          <Text>Failed to load documentation. Please try again later.</Text>
        </Box>
      </PageTemplate>
    );
  }

  return (
    <PageTemplate title="API Documentation">
      <Tabs
        items={availableTabs.map((tab) => {
          const spec =
            tab === "data-model"
              ? docs?.data_model_documentation
              : docs?.module_documentation?.[tab];

          return {
            key: tab,
            label:
              tab === "data-model"
                ? "Data Model API"
                : `${formatAnyName(tab)} API`,
            content: spec ? (
              renderDocumentation(spec)
            ) : (
              <Box p="4">
                <Text>No documentation available for this section.</Text>
              </Box>
            ),
          };
        })}
        selectedTab={selectedDocType}
      />
    </PageTemplate>
  );
}
